/**
 * The one place a profile's completeness is scored.
 *
 * `Profile.profileCompleteness` is a Postgres GENERATED column and stays a flat
 * count of six columns — it cannot join `profile_sections`, so it can never see
 * the resume half of a profile. This module is the weighted score the product
 * actually shows: the api resolver, the activity-notification email and the
 * Town Square meter all read it, so an artist is never told two different
 * numbers.
 *
 * Weights are a retention ranking, not a field census. Avatar, bio and artist
 * statement each show 10–11× retention lift in cohort data and carry more than
 * twice the weight of anything else; the resume sections matter but matter less.
 * They sum to exactly 100, so the score IS the sum of the earned weights and no
 * normalisation step can drift.
 */

import type { ProfileSectionKind } from './profile-sections';

/** The resume kinds that count toward the score, in retention order. */
export const SCORED_SECTION_KINDS = [
  'gallery_representation',
  'education',
  'exhibition',
  'press',
  'career',
] as const satisfies readonly ProfileSectionKind[];

export type ScoredSectionKind = (typeof SCORED_SECTION_KINDS)[number];

export type CompletenessFieldKey =
  | 'avatar'
  | 'bio'
  | 'artistStatement'
  | 'displayName'
  | 'categories'
  | 'socialLinks'
  | 'banner'
  | 'location'
  | ScoredSectionKind;

export type CompletenessWeight = {
  key: CompletenessFieldKey;
  weight: number;
  /**
   * English label, used by surfaces with no message catalog (the notification
   * email). Town Square renders `profile.completeness.field.<key>` instead, so a
   * localized page never falls back to this string.
   */
  label: string;
};

/**
 * Declaration order IS the tie-break order for "the single most valuable missing
 * field", so equal-weight fields resolve deterministically rather than by
 * whatever order a caller's object keys happen to iterate in.
 */
export const PROFILE_COMPLETENESS_WEIGHTS: readonly CompletenessWeight[] = [
  { key: 'avatar', weight: 18, label: 'Profile photo' },
  { key: 'bio', weight: 18, label: 'Bio' },
  { key: 'artistStatement', weight: 18, label: 'Artist statement' },
  { key: 'displayName', weight: 8, label: 'Your name' },
  { key: 'categories', weight: 8, label: 'What you make' },
  { key: 'socialLinks', weight: 6, label: 'Links' },
  { key: 'banner', weight: 6, label: 'Banner image' },
  { key: 'location', weight: 3, label: 'Location' },
  { key: 'gallery_representation', weight: 3, label: 'Gallery representation' },
  { key: 'education', weight: 3, label: 'Education' },
  { key: 'exhibition', weight: 3, label: 'Exhibitions' },
  { key: 'press', weight: 3, label: 'Press' },
  { key: 'career', weight: 3, label: 'Career highlights' },
] as const;

/** The score a fully-completed profile earns. Asserted in the spec. */
export const PROFILE_COMPLETENESS_MAX = PROFILE_COMPLETENESS_WEIGHTS.reduce((memo, field) => memo + field.weight, 0);

const LABEL_BY_KEY: Record<string, string> = PROFILE_COMPLETENESS_WEIGHTS.reduce<Record<string, string>>(
  (memo, field) => {
    memo[field.key] = field.label;
    return memo;
  },
  {}
);

/** English label for a field key — `null` for anything not scored. */
export function completenessFieldLabel(key: string | null | undefined): string | null {
  if (!key) return null;
  return LABEL_BY_KEY[key] ?? null;
}

export type CompletenessSection = {
  kind: string;
  items?: ReadonlyArray<unknown> | null;
  /**
   * The artist said this section does not apply to them (#4153). It earns the
   * section's weight exactly as an entry would: an artist with no gallery, no
   * degree and no press has nothing to add, and a score that can never reach 100
   * for them only tells them to invent a resume.
   */
  isNotApplicable?: boolean | null;
};

/**
 * Everything the score reads. Every field is optional: the email job, the
 * resolver and the page each hand over the subset they already loaded, and an
 * absent field simply scores as incomplete.
 */
export type CompletenessProfile = {
  avatarUrl?: string | null;
  bio?: string | null;
  artistStatement?: string | null;
  displayName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  bannerUrl?: string | null;
  socialLinks?: ReadonlyArray<unknown> | null;
  categories?: ReadonlyArray<unknown> | null;
  city?: string | null;
  region?: string | null;
  country?: string | null;
  sections?: ReadonlyArray<CompletenessSection> | null;
};

export type ProfileCompletenessResult = {
  /** 0–100. The sum of the earned weights, which is why it needs no rounding. */
  score: number;
  /** Incomplete field keys, most valuable first. */
  missingFields: CompletenessFieldKey[];
  /** The single most valuable missing field — `null` only at 100%. */
  nextField: CompletenessFieldKey | null;
};

function hasText(value: unknown): boolean {
  return typeof value === 'string' && value.trim() !== '';
}

function hasEntries(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

/**
 * A name counts when the profile carries a display name, or EITHER a first or a
 * last name. That `or` is not a looser rule chosen for kindness — it is what
 * `getDisplayName` falls back to, so requiring both would dock an artist eight
 * points and ask them to add a name their own `<h1>` is already rendering.
 */
function hasName(profile: CompletenessProfile): boolean {
  if (hasText(profile.displayName)) return true;
  return hasText(profile.firstName) || hasText(profile.lastName);
}

/**
 * A resume kind counts when its section carries at least one item, or when the
 * artist marked it "doesn't apply to me".
 */
function hasSection(profile: CompletenessProfile, kind: ScoredSectionKind): boolean {
  const sections = profile.sections;
  if (!sections?.length) return false;
  return sections.some(
    (section) => section && section.kind === kind && (hasEntries(section.items) || section.isNotApplicable === true)
  );
}

/** Whether a resume kind can be marked "doesn't apply to me" — only the scored ones can. */
export function isScoredSectionKind(kind: string): kind is ScoredSectionKind {
  return (SCORED_SECTION_KINDS as readonly string[]).includes(kind);
}

/** The weight a field earns, so a checklist can say what finishing it is worth. */
export function completenessFieldWeight(key: CompletenessFieldKey): number {
  return PROFILE_COMPLETENESS_WEIGHTS.find((field) => field.key === key)?.weight ?? 0;
}

function isFieldComplete(profile: CompletenessProfile, key: CompletenessFieldKey): boolean {
  switch (key) {
    case 'avatar':
      return hasText(profile.avatarUrl);
    case 'bio':
      return hasText(profile.bio);
    case 'artistStatement':
      return hasText(profile.artistStatement);
    case 'displayName':
      return hasName(profile);
    case 'categories':
      return hasEntries(profile.categories);
    case 'socialLinks':
      return hasEntries(profile.socialLinks);
    case 'banner':
      return hasText(profile.bannerUrl);
    case 'location':
      return hasText(profile.city) || hasText(profile.region) || hasText(profile.country);
    default:
      return hasSection(profile, key);
  }
}

/**
 * Scores a profile and names what to finish next.
 *
 * `missingFields` is already ordered by weight (declaration order), so
 * `nextField` is its head rather than a second scan with its own tie-break rule.
 */
export function profileCompleteness(profile: CompletenessProfile | null | undefined): ProfileCompletenessResult {
  const subject = profile ?? {};

  const { score, missingFields } = PROFILE_COMPLETENESS_WEIGHTS.reduce<{
    score: number;
    missingFields: CompletenessFieldKey[];
  }>(
    (memo, field) => {
      if (isFieldComplete(subject, field.key)) {
        memo.score += field.weight;
        return memo;
      }
      memo.missingFields.push(field.key);
      return memo;
    },
    { score: 0, missingFields: [] }
  );

  return { score, missingFields, nextField: missingFields[0] ?? null };
}
